/**
 * Location Service
 *
 * A reusable wrapper around the browser's Geolocation API that is also
 * compatible with Capacitor's WebView environment.  The same service can be
 * used anywhere in the app (timesheet clock-in/out, orchard block planning,
 * vehicle/tractor tracking, etc.).
 */

export interface LocationCoords {
  latitude: number;
  longitude: number;
  accuracy: number;          // metres
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
}

export interface LocationResult {
  coords: LocationCoords;
  timestamp: number;
}

export type LocationError =
  | { code: 'PERMISSION_DENIED'; message: string }
  | { code: 'POSITION_UNAVAILABLE'; message: string }
  | { code: 'TIMEOUT'; message: string }
  | { code: 'UNSUPPORTED'; message: string };

export type WatchId = number;

const DEFAULT_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10_000,
  maximumAge: 30_000,
};

function mapGeolocationError(err: GeolocationPositionError): LocationError {
  switch (err.code) {
    case GeolocationPositionError.PERMISSION_DENIED:
      return { code: 'PERMISSION_DENIED', message: 'Location permission denied by user.' };
    case GeolocationPositionError.POSITION_UNAVAILABLE:
      return { code: 'POSITION_UNAVAILABLE', message: 'Location information is unavailable.' };
    case GeolocationPositionError.TIMEOUT:
      return { code: 'TIMEOUT', message: 'Location request timed out.' };
    default:
      return { code: 'POSITION_UNAVAILABLE', message: err.message };
  }
}

function positionToResult(pos: GeolocationPosition): LocationResult {
  return {
    coords: {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude,
      altitudeAccuracy: pos.coords.altitudeAccuracy,
      heading: pos.coords.heading,
      speed: pos.coords.speed,
    },
    timestamp: pos.timestamp,
  };
}

class LocationService {
  /**
   * Returns true when the Geolocation API is available in the current
   * environment (browser or Capacitor WebView).
   */
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'geolocation' in navigator;
  }

  /**
   * Fetch a single position reading.
   *
   * @param options  Optional override of the default PositionOptions.
   * @returns        A resolved LocationResult or a rejected LocationError.
   */
  getCurrentPosition(options?: PositionOptions): Promise<LocationResult> {
    if (!this.isSupported()) {
      return Promise.reject<LocationResult>({
        code: 'UNSUPPORTED',
        message: 'Geolocation is not supported in this environment.',
      } satisfies LocationError);
    }

    return new Promise<LocationResult>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        pos => resolve(positionToResult(pos)),
        err => reject(mapGeolocationError(err)),
        { ...DEFAULT_OPTIONS, ...options },
      );
    });
  }

  /**
   * Begin watching for position changes.
   *
   * @param onSuccess  Called each time a new position is available.
   * @param onError    Called when an error occurs.
   * @param options    Optional override of the default PositionOptions.
   * @returns          A watch-id that can be passed to `clearWatch()`.
   */
  watchPosition(
    onSuccess: (result: LocationResult) => void,
    onError: (error: LocationError) => void,
    options?: PositionOptions,
  ): WatchId {
    if (!this.isSupported()) {
      onError({ code: 'UNSUPPORTED', message: 'Geolocation is not supported in this environment.' });
      return -1;
    }

    return navigator.geolocation.watchPosition(
      pos => onSuccess(positionToResult(pos)),
      err => onError(mapGeolocationError(err)),
      { ...DEFAULT_OPTIONS, ...options },
    );
  }

  /**
   * Stop watching for position changes.
   *
   * @param watchId  The id returned by `watchPosition()`.
   */
  clearWatch(watchId: WatchId): void {
    if (this.isSupported() && watchId >= 0) {
      navigator.geolocation.clearWatch(watchId);
    }
  }
}

export const locationService = new LocationService();
export default locationService;
